import React from "react";
import gsap from "gsap";
import { Num, type RankedPlayer } from "@uno/protocol";

const SELECTORS = {
    header: ".ge-header",
    player: ".ge-player",
    buttons: ".ge-buttons",
    miniCard: ".ge-card-mini",
    group: ".ge-group",
    subtotal: ".ge-subtotal",
    score: ".ge-score",
    weeklyImpactSlot: ".ge-weekly-impact-slot",
    weeklyImpact: ".ge-weekly-impact",
    weeklyScore: ".ge-weekly-score",
    weeklyRank: ".ge-weekly-rank",
    weeklyGaugeSegment: ".ge-weekly-gauge-segment",
};

const TIMING = {
    headerDuration: 0.5,
    playerDuration: 0.55,
    playerStagger: 0.06,
    revealDelay: 0.15,
    cardStagger: 0.08,
    cardDuration: 0.4,
    buttonsDelay: 0.2,
    buttonsDuration: 0.4,
};

const formatSignedScore = (value: number) => {
    const rounded = Math.round(value);
    return rounded > 0 ? `+${rounded}` : rounded.toString();
};

const useGameEndAnimation = (
    containerRef: React.RefObject<HTMLDivElement>,
    players: RankedPlayer[],
) => {
    const timelineRef = React.useRef<gsap.core.Timeline | null>(null);
    const hasAnimatedRef = React.useRef(false);

    React.useLayoutEffect(() => {
        const container = containerRef.current;

        if(!container || players.length === 0 || hasAnimatedRef.current) {
            return;
        }

        hasAnimatedRef.current = true;

        const ctx = gsap.context(() => {
            const timeline = gsap.timeline();
            timelineRef.current = timeline;

            const headerEls = gsap.utils.toArray<HTMLElement>(SELECTORS.header);
            const playerEls = gsap.utils.toArray<HTMLElement>(SELECTORS.player);
            const buttonEls = gsap.utils.toArray<HTMLElement>(SELECTORS.buttons);
            const allCardEls = gsap.utils.toArray<HTMLElement>(SELECTORS.miniCard);
            const weeklyImpactSlots = gsap.utils.toArray<HTMLElement>(SELECTORS.weeklyImpactSlot);
            const weeklyImpactEls = gsap.utils.toArray<HTMLElement>(SELECTORS.weeklyImpact);

            // Initial states — widget slot is display:none so it occupies no layout space
            // until its reveal animation runs, flipping it back to flex (preserves the
            // centering classes applied via Tailwind).
            gsap.set(headerEls, { opacity: 0, y: -15 });
            gsap.set(playerEls, { opacity: 0, y: 30 });
            gsap.set(allCardEls, { opacity: 0, scale: 0.4 });
            gsap.set(buttonEls, { opacity: 0, y: 20 });
            gsap.set(weeklyImpactSlots, { display: "none" });
            gsap.set(weeklyImpactEls, { opacity: 0, y: -20, scale: 0.95 });

            // 1. Header (pill) slides down
            timeline.to(headerEls, {
                opacity: 1,
                y: 0,
                duration: TIMING.headerDuration,
                ease: "power2.out",
            });

            // 2. Player cards slide up, slightly overlapping the header fade-in
            timeline.to(playerEls, {
                opacity: 1,
                y: 0,
                duration: TIMING.playerDuration,
                stagger: TIMING.playerStagger,
                ease: "back.out(1.4)",
            }, "-=0.25");

            // 3. Reveal phase — cards, subtotals, and total scores animate in parallel per player
            timeline.addLabel("reveal", `+=${TIMING.revealDelay}`);

            playerEls.forEach((playerEl) => {
                revealPlayer(timeline, playerEl);
            });

            // 4. Action buttons fade in at the end
            timeline.to(buttonEls, {
                opacity: 1,
                y: 0,
                duration: TIMING.buttonsDuration,
                ease: "power2.out",
            }, `+=${TIMING.buttonsDelay}`);

            // 5. Weekly impact widget slides in, then runs its internal animations
            weeklyImpactEls.forEach((widget) => {
                revealWeeklyImpact(timeline, widget);
            });

        }, container);

        return () => {
            ctx.revert();
        };
    }, [containerRef, players]);

    const skip = React.useCallback(() => {
        const timeline = timelineRef.current;

        if(timeline && timeline.progress() < 1) {
            timeline.progress(1);
        }
    }, []);

    return { skip };
};

const revealPlayer = (timeline: gsap.core.Timeline, playerEl: HTMLElement) => {
    const groupEls = playerEl.querySelectorAll<HTMLElement>(SELECTORS.group);
    const scoreEl = playerEl.querySelector<HTMLElement>(SELECTORS.score);
    const playerCards = playerEl.querySelectorAll<HTMLElement>(SELECTORS.miniCard);

    let cardIdx = 0;

    groupEls.forEach((groupEl) => {
        const groupCards = groupEl.querySelectorAll<HTMLElement>(SELECTORS.miniCard);
        const subtotalEl = groupEl.querySelector<HTMLElement>(SELECTORS.subtotal);
        const groupStartIdx = cardIdx;

        // Reveal each card with a staggered delay based on its global position in the player's hand
        groupCards.forEach((cardEl) => {
            timeline.to(cardEl, {
                opacity: 1,
                scale: 1,
                duration: TIMING.cardDuration,
                ease: "back.out(1.7)",
            }, `reveal+=${cardIdx * TIMING.cardStagger}`);
            cardIdx++;
        });

        // Group subtotal — count up alongside this group's card reveals
        if(subtotalEl && groupCards.length > 0) {
            animateCounter(timeline, subtotalEl, subtotalEl.dataset.finalSubtotal, {
                duration: (groupCards.length - 1) * TIMING.cardStagger + TIMING.cardDuration,
                position: `reveal+=${groupStartIdx * TIMING.cardStagger}`,
                format: (value) => Math.round(value).toString(),
            });
        }
    });

    // Total score — counts up across the whole reveal phase
    if(scoreEl) {
        const totalDuration = playerCards.length > 1
            ? (playerCards.length - 1) * TIMING.cardStagger + TIMING.cardDuration
            : 0.6;

        animateCounter(timeline, scoreEl, scoreEl.dataset.finalScore, {
            duration: totalDuration,
            position: "reveal",
            format: formatSignedScore,
        });
    }
};

type CounterOptions = {
    duration: number;
    position: string;
    format: (value: number) => string;
};

const animateCounter = (
    timeline: gsap.core.Timeline,
    element: HTMLElement,
    finalValueRaw: string | undefined,
    { duration, position, format }: CounterOptions,
) => {
    const finalValue = parseFloat(finalValueRaw ?? "0");

    if(finalValue === 0) {
        return;
    }

    element.textContent = "0";
    const counter = { value: 0 };

    timeline.to(counter, {
        value: finalValue,
        duration,
        ease: "power1.out",
        onUpdate: () => {
            element.textContent = format(counter.value);
        },
    }, position);
};

const revealWeeklyImpact = (timeline: gsap.core.Timeline, widget: HTMLElement) => {
    const slot = widget.closest<HTMLElement>(SELECTORS.weeklyImpactSlot);

    if(slot) {
        timeline.set(slot, { display: "flex" }, "+=0.3");
    }

    timeline.to(widget, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.5,
        ease: "back.out(1.6)",
    }, slot ? ">" : "+=0.3");

    timeline.addLabel("weekly");

    // Score counter: before → after
    const scoreEl = widget.querySelector<HTMLElement>(SELECTORS.weeklyScore);

    if(scoreEl) {
        const before = parseFloat(scoreEl.dataset.scoreBefore ?? "0");
        const after = parseFloat(scoreEl.dataset.scoreAfter ?? "0");

        if(before !== after) {
            scoreEl.textContent = Num.compact(Math.round(before));
            const counter = { value: before };

            timeline.to(counter, {
                value: after,
                duration: 0.8,
                ease: "power2.out",
                onUpdate: () => {
                    scoreEl.textContent = Num.compact(Math.round(counter.value));
                },
            }, "weekly+=0.1");
        }
    }

    // Rank flip: before → after
    const rankEl = widget.querySelector<HTMLElement>(SELECTORS.weeklyRank);

    if(rankEl) {
        const beforeRaw = rankEl.dataset.rankBefore ?? "";
        const afterRaw = rankEl.dataset.rankAfter ?? "";

        if(beforeRaw !== afterRaw) {
            rankEl.textContent = beforeRaw ? beforeRaw : "—";

            timeline.to(rankEl, {
                y: -8,
                opacity: 0,
                duration: 0.18,
                ease: "power2.in",
                onComplete: () => {
                    rankEl.textContent = afterRaw ? afterRaw : "—";
                },
            }, "weekly+=0.35");

            timeline.fromTo(rankEl, {
                y: 8,
                opacity: 0,
            }, {
                y: 0,
                opacity: 1,
                duration: 0.22,
                ease: "back.out(1.8)",
            }, ">");
        }
    }

    // Gauge segments fill one by one
    const gaugeSegments = Array.from(widget.querySelectorAll<HTMLElement>(SELECTORS.weeklyGaugeSegment));

    gaugeSegments.forEach((segment, index) => {
        timeline.fromTo(segment, {
            scale: 1,
        }, {
            scale: 1.3,
            duration: 0.1,
            ease: "power1.out",
            yoyo: true,
            repeat: 1,
            transformOrigin: "center",
        }, `weekly+=${0.15 + index * 0.08}`);
    });
};

export default useGameEndAnimation;
